import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, useInView, useMotionValue, useSpring } from 'framer-motion';
import { useAuth0 } from '@auth0/auth0-react';
import './chatTheme.css';
import './landing_redesign.css';

// Assets (pas paden aan indien nodig)
import humanaizeLogo from './images/footerlogo.webp';
import jesseAvatar from './images/Jesse_avatar.webp';
import jessePhoto from './images/Jesse_avatar_foto.webp';
import jessePortrait from './images/Jesse-Geul.jpeg.webp';
import jesseCongres from './images/jesse-congres.webp';
import jesseSmile from './images/1732694684763.webp';
import Media1 from './videos/Intro_website.mp4';
import screen1 from './images/Afbeelding1.webp';
import screen2 from './images/Afbeelding2.webp';

/* ---------- Anim helpers ---------- */
const revealParent = {
  hidden: { opacity: 0, y: 12 },
  show: {
    opacity: 1, y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1], when: 'beforeChildren', staggerChildren: 0.06 }
  }
};
const revealItem = {
  hidden: { opacity: 0, y: 16, scale: 0.98 },
  show:   { opacity: 1, y: 0,  scale: 1, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } }
};

// Magnetic CTA
function MagneticButton({ children, className = '', ...rest }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 400, damping: 30, mass: 0.3 });
  const springY = useSpring(y, { stiffness: 400, damping: 30, mass: 0.3 });

  return (
    <motion.button
      ref={ref}
      className={`btn-cta ${className}`}
      style={{ x: springX, y: springY }}
      onMouseMove={(e) => {
        const r = ref.current?.getBoundingClientRect(); if (!r) return;
        const relX = e.clientX - (r.left + r.width / 2);
        const relY = e.clientY - (r.top + r.height / 2);
        const max = 12;
        x.set((relX / (r.width / 2)) * max);
        y.set((relY / (r.height / 2)) * max);
      }}
      onMouseLeave={() => { x.set(0); y.set(0); }}
      {...rest}
    >
      {children}
    </motion.button>
  );
}

// Spotlight (mouse-follow)
function useSpotlight() {
  const rootRef = useRef(null);
  useEffect(() => {
    const el = rootRef.current; if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
      el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
    };
    el.addEventListener('pointermove', onMove);
    return () => el.removeEventListener('pointermove', onMove);
  }, []);
  return rootRef;
}

/* ---------- FAQ card (chevron + motion layout) ---------- */
function FAQCard({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      className="faq-card"
      layout
      initial={false}
      aria-expanded={open}
      role="button"
      tabIndex={0}
      onClick={() => setOpen(!open)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setOpen(!open)}
    >
      <div className="faq-head">
        <h3 className="faq-q">{q}</h3>
        <div className="faq-chevron">
          {/* chevron-down; roteert 180° bij open */}
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" fill="currentColor"/>
          </svg>
        </div>
      </div>

      <motion.div
        className="faq-body"
        layout
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: open ? 1 : 0, height: open ? 'auto' : 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <p>{a}</p>
      </motion.div>
    </motion.div>
  );
}

/* ---------- Privacy Modal Component ---------- */
function PrivacyModal({ isVisible, onClose }) {
  if (!isVisible) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content help-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <span className="material-icons">close</span>
        </button>

        <div className="modal-icon-header info">
          <span className="material-icons">shield</span>
        </div>

        <h2>Privacy</h2>

        <div className="help-section">
          <div className="section-icon">
            <span className="material-icons">info</span>
          </div>
          <div className="section-content">
            <h3>Wat we verzamelen</h3>
            <p>HAI Jesse verzamelt minimale informatie:</p>
            <ul>
              <li>Account informatie (naam, email) voor authenticatie</li>
              <li>Technische gegevens zoals IP-adres en browser type voor beveiliging</li>
            </ul>
          </div>
        </div>

        <div className="help-section">
          <div className="section-icon">
            <span className="material-icons">lock</span>
          </div>
          <div className="section-content">
            <h3>Je gesprekken blijven privé</h3>
            <p>We waarderen je privacy enorm:</p>
            <ul>
              <li><strong>Gesprekken worden NIET opgeslagen</strong> - na je sessie worden ze verwijderd</li>
              <li><strong>Geen AI-training</strong> - je data wordt niet gebruikt om modellen te trainen</li>
              <li>Alle communicatie is versleuteld met HTTPS/TLS</li>
              <li>We delen je data nooit met derden</li>
            </ul>
          </div>
        </div>

        <div className="help-section">
          <div className="section-icon">
            <span className="material-icons">verified_user</span>
          </div>
          <div className="section-content">
            <h3>Je rechten (AVG)</h3>
            <p>Je hebt volledige controle over je accountgegevens:</p>
            <ul>
              <li>Recht op inzage van je account informatie</li>
              <li>Recht op correctie van onjuiste informatie</li>
              <li>Recht op verwijdering van je account</li>
              <li>Gesprekken worden automatisch verwijderd na je sessie</li>
            </ul>
          </div>
        </div>

        <div className="help-section">
          <div className="section-icon">
            <span className="material-icons">mail</span>
          </div>
          <div className="section-content">
            <h3>Contact</h3>
            <p>Voor vragen over privacy kun je contact opnemen via:</p>
            <ul>
              <li><strong>Email:</strong> info@humanaize.nl</li>
              <li><strong>Website:</strong> <a href="https://humanaize.nl" target="_blank" rel="noopener noreferrer">humanaize.nl</a></li>
            </ul>
          </div>
        </div>

        <button className="modal-button primary" onClick={onClose}>
          Begrepen
        </button>
      </div>
    </div>
  );
}

/* ---------- Terms Modal Component ---------- */
function TermsModal({ isVisible, onClose }) {
  if (!isVisible) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content help-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <span className="material-icons">close</span>
        </button>

        <div className="modal-icon-header info">
          <span className="material-icons">description</span>
        </div>

        <h2>Voorwaarden</h2>

        <div className="help-section">
          <div className="section-icon">
            <span className="material-icons">handshake</span>
          </div>
          <div className="section-content">
            <h3>Dienstverlening</h3>
            <p>HAI Jesse is een AI-aangedreven tool voor het schrijven van vacatureteksten:</p>
            <ul>
              <li>We leveren de dienst naar beste vermogen</li>
              <li>De gegenereerde content is een hulpmiddel, geen garantie</li>
              <li>Je bent zelf verantwoordelijk voor de eindversie die je publiceert</li>
              <li>We streven naar 99% uptime, maar kunnen dit niet garanderen</li>
              <li>Je gesprekken worden niet opgeslagen na je sessie</li>
            </ul>
          </div>
        </div>

        <div className="help-section">
          <div className="section-icon">
            <span className="material-icons">payments</span>
          </div>
          <div className="section-content">
            <h3>Abonnement en betaling</h3>
            <ul>
              <li>Abonnementen worden maandelijks of jaarlijks automatisch verlengd</li>
              <li>Je kunt op elk moment opzeggen zonder opzegtermijn</li>
              <li>Bij opzegging blijft je account actief tot het einde van de betaalde periode</li>
              <li>Restitutie is mogelijk binnen 14 dagen na aankoop</li>
            </ul>
          </div>
        </div>

        <div className="help-section">
          <div className="section-icon">
            <span className="material-icons">copyright</span>
          </div>
          <div className="section-content">
            <h3>Intellectueel eigendom</h3>
            <ul>
              <li>De content die Jesse genereert is jouw eigendom</li>
              <li>Je mag deze vrijelijk gebruiken voor commerciële doeleinden</li>
              <li>De software en AI-technologie blijven eigendom van HumanAIze</li>
              <li>Het is niet toegestaan om Jesse te reverse-engineeren</li>
            </ul>
          </div>
        </div>

        <div className="help-section">
          <div className="section-icon">
            <span className="material-icons">gavel</span>
          </div>
          <div className="section-content">
            <h3>Aansprakelijkheid</h3>
            <ul>
              <li>We zijn niet aansprakelijk voor indirecte schade door gebruik van de dienst</li>
              <li>Maximale aansprakelijkheid is beperkt tot het abonnementsbedrag</li>
              <li>We behouden het recht om accounts te blokkeren bij misbruik</li>
              <li>Nederlandse wet is van toepassing</li>
            </ul>
          </div>
        </div>

        <div className="help-section">
          <div className="section-icon">
            <span className="material-icons">contact_support</span>
          </div>
          <div className="section-content">
            <h3>Vragen?</h3>
            <p>Voor vragen over de voorwaarden kun je contact opnemen:</p>
            <ul>
              <li><strong>Email:</strong> info@humanaize.nl</li>
              <li><strong>Website:</strong> <a href="https://humanaize.nl" target="_blank" rel="noopener noreferrer">humanaize.nl</a></li>
            </ul>
          </div>
        </div>

        <button className="modal-button primary" onClick={onClose}>
          Begrepen
        </button>
      </div>
    </div>
  );
}

/* ---------- Helpers: image slot + collage (video blijft) ---------- */
function ImageSlot({ src, alt, label, caption }) {
  const [broken, setBroken] = React.useState(false);
  return (
    <div className="collage-item">
      {!broken ? (
        <img src={src} alt={alt} loading="lazy" onError={() => setBroken(true)} />
      ) : (
        <div className="collage-placeholder">
          <span>{label}</span>
        </div>
      )}
      {caption ? <figcaption>{caption}</figcaption> : null}
    </div>
  );
}

function ScreenshotsCollage() {
  // Gebruik paden uit de /public folder (werkt ook als de bestanden nog niet bestaan)
  // Zet je bestanden later in: public/images/collage-1.jpg en public/images/collage-2.jpg
  const img1 = screen1;
  const img2 = screen2;

  return (
    <section className="lp-section" aria-labelledby="screenshots-title">
      <h2 id="screenshots-title" className="section-title">Zo werkt HAI Jesse</h2>
      <div className="lp-collage">
        <figure className="collage-grid">
          <ImageSlot
            src={img1}
            alt="Start het gesprek met Jesse en geef je input"
            label="Upload /images/collage-1.jpg"
            caption="Stap 1: start het gesprek en deel je input"
          />
          <ImageSlot
            src={img2}
            alt="Jesse zet je input om naar een sterke vacaturetekst"
            label="Upload /images/collage-2.jpg"
            caption="Stap 2: praat met Jesse voor een sterke vacaturetekst"
          />
        </figure>
      </div>
    </section>
  );
}

/* ---------- Screenshot Marquee (bridge between Origin & Plans) ---------- */
function ScreenshotMarquee() {
  const screens = [
    { icon: 'chat', label: 'Chat met Jesse', color: '#0d9488' },
    { icon: 'description', label: 'Vacaturetekst review', color: '#14b8a6' },
    { icon: 'cloud_upload', label: 'Upload bestaande tekst', color: '#0f766e' },
    { icon: 'picture_as_pdf', label: 'Professionele PDF export', color: '#0ea5e9' },
    { icon: 'auto_fix_high', label: 'AI verfijning via chat', color: '#10b981' },
    { icon: 'analytics', label: 'Resultaat in seconden', color: '#f59e0b' },
  ];
  const items = [...screens, ...screens]; // duplicate for seamless loop
  return (
    <div className="lp-marquee-section">
      <div className="lp-marquee">
        <div className="lp-marquee__track">
          {items.map((s, i) => (
            <div key={i} className="lp-marquee__item" style={{ '--card-accent': s.color }}>
              <span className="material-icons">{s.icon}</span>
              <span className="lp-marquee__label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Abonnementen ---------- */
function PlanCards({ selected, onSelect, onContinue }) {
  const plans = [
    {
      id: 'personal',
      name: 'Personal',
      price: '€ 49 / maand',
      bullet: ['1 gebruiker', 'Volledige chat + tools', 'Eenvoudige start'],
      cta: 'Kies Personal',
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 'Maatwerk offerte',
      bullet: ['Meerdere seats', 'Beveiliging & support', 'Integraties / branding'],
      cta: 'Kies Enterprise',
    },
  ];

  return (
    <section className="lp-section" aria-labelledby="plans-title">
      <h2 id="plans-title" className="section-title">Kies je abonnement</h2>
      <div className="plans-grid" role="list">
        {plans.map(p => (
          <article key={p.id} className={`plan-card ${selected === p.id ? 'is-selected' : ''}`} role="listitem">
            <header className="plan-head">
              <h3>{p.name}</h3>
              <div className="plan-price">{p.price}</div>
            </header>
            <ul className="plan-bullets">
              {p.bullet.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
            <button
              type="button"
              className="btn-cta plan-choose"
              aria-pressed={selected === p.id}
              onClick={() => { onSelect(p.id); onContinue?.(); }}
            >
              {p.cta}
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}


/* ---------- Page ---------- */
export default function Landing() {
  const { loginWithRedirect, isAuthenticated } = useAuth0();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Handle referer parameter for trial ending redirect
  // When Stripe sends users to ?referer=free_trial_ending, redirect to account page
  useEffect(() => {
    const referer = searchParams.get('referer');

    if (referer === 'free_trial_ending') {
      console.log('📧 Trial ending referer detected, redirecting to account...');

      if (isAuthenticated) {
        // User is logged in, go directly to account page
        navigate('/account');
      } else {
        // User not logged in, store referer and trigger login
        sessionStorage.setItem('post_login_redirect', '/account');
        loginWithRedirect({
          appState: { returnTo: '/account' },
        });
      }
    }
  }, [searchParams, isAuthenticated, navigate, loginWithRedirect]);

  // Anonymous checkout handler
  const handleAnonymousCheckout = async (e) => {
    if (e) e.preventDefault();

    // Validation - use form state (from trial form)
    if (!form.email || !form.email.includes('@')) {
      setCheckoutError('Voer een geldig email adres in');
      return;
    }
    if (!form.name || form.name.trim().length < 2) {
      setCheckoutError('Voer je volledige naam in');
      return;
    }

    setCheckoutLoading(true);
    setCheckoutError('');

    try {
      const response = await fetch(`${API_URL}/api/checkout/anonymous`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: form.email,
          name: form.name,
          plan: 'personal'
        })
      });

      const data = await response.json();

      if (data.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setCheckoutError(error.message || 'Er is iets misgegaan. Probeer het opnieuw.');
      setCheckoutLoading(false);
    }
  };

  useEffect(() => {
    document.title = 'HAI Job — Schrijf vacatureteksten die kandidaten aanspreken';
    const meta = document.querySelector('meta[name="description"]') || document.createElement('meta');
    meta.name = 'description';
    meta.content = 'HAI Job: maak in minuten pakkende vacatureteksten met AI. Praat, typ of upload — Job maakt een professionele tekst. Probeer gratis.';
    if (!meta.parentNode) document.head.appendChild(meta);
  }, []);

  // Video (vul later in)
  const videoType = 'none'; // 'none' | 'youtube' | 'mp4'
  const videoSrc  = '';
  const [plan, setPlan] = useState('personal'); // 'personal' | 'enterprise'

  // Modal states
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);


  // Trial/Checkout form (shared state)
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const mailtoHref = useMemo(() => {
    const to = encodeURIComponent('mano.radema@humanaize.nl, info@jessegeul.nl'); 
    const subject = encodeURIComponent(
      `Interesse: HAI Jesse – ${plan === 'personal' ? 'Personal' : 'Enterprise'}`
    );

    const planLabel =
      plan === 'personal'
        ? 'Personal (€49 per maand)'
        : 'Enterprise (maatwerk offerte)';

    const body = encodeURIComponent(
  `Hi Jesse-team,

  Ik wil graag een proefaccount en stel het abonnement alvast in op:
  Abonnement: ${planLabel}

  Naam: ${form.name || '-'}
  E-mail: ${form.email || '-'}
  Telefoon: ${form.phone || '-'}

  Groet!`
    );
    return `mailto:${to}?subject=${subject}&body=${body}`;
  }, [form, plan]);

  // Features
  const features = [
    {
      icon: 'auto_awesome',
      title: 'Vacatures in de stijl van Jesse',
      text: 'De tone-of-voice van de beste recruiter van NL: helder, menselijk en to-the-point.',
      accent: 'blue',
    },
    {
      icon: 'record_voice_over',
      title: 'Van belletje naar tekst in 10 minuten',
      text: 'Geen uren schrijven. Een kort gesprek volstaat en je copy staat er binnen 10 minuten.',
      accent: 'green',
    },
    {
      icon: 'auto_fix_high',
      title: 'Verbeter je bestaande teksten',
      text: 'Optimaliseer op structuur, focus en aantrekkingskracht. Plakken, verbeteren, klaar.',
      accent: 'purple',
    },
  ];

  const heroRef = useSpotlight();
  const featRef = useRef(null);
  const featInView = useInView(featRef, { margin: '-10% 0px -10% 0px', once: true });

  return (
    <main className="landing">
      {/* ===== TOP NAV ===== */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <div className="lp-nav-brand">
            <img className="lp-nav-logo" src={jesseAvatar} alt="" />
            <span className="lp-nav-name">Jesse</span>
          </div>
          <div className="lp-nav-links">
            <a href="#features" className="lp-nav-link">Hoe het werkt</a>
            <a href="#origin" className="lp-nav-link">Over Jesse</a>
            <a href="#signup" className="lp-nav-link">Prijzen</a>
          </div>
          <div className="lp-nav-actions">
            {!isAuthenticated ? (
              <button
                className="lp-nav-cta"
                onClick={() => loginWithRedirect({ appState: { returnTo: '/app' } })}
              >
                Inloggen
              </button>
            ) : (
              <Link className="lp-nav-cta" to="/app">
                Open Chat
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* ===== HERO — Two-column: video left, text right, congres photo bg ===== */}
      <header className="lp-hero" ref={heroRef}>
        <img className="lp-hero-bg" src={jesseCongres} alt="" aria-hidden="true" />
        <div className="lp-hero-overlay" />
        <motion.div
          className="lp-hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        >
          {/* Left column: video phone */}
          <div className="lp-hero-video">
            <div className="portrait-wrap">
              <video
                className="portrait-bg"
                src={Media1}
                autoPlay
                muted
                loop
                playsInline
                aria-hidden="true"
                tabIndex={-1}
              />
              <div className="portrait-phone">
                <video
                  className="portrait-player"
                  src={Media1}
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  controlsList="nodownload noplaybackrate"
                  aria-label="Introductievideo van Jesse"
                />
                <button
                  type="button"
                  className="unmute-hint"
                  onClick={(e) => {
                    const v = e.currentTarget.parentElement?.querySelector('.portrait-player');
                    if (v) {
                      v.muted = false;
                      v.play().catch(() => {});
                    }
                    e.currentTarget.style.display = 'none';
                  }}
                  title="Geluid aan"
                >
                  Geluid aan
                </button>
              </div>
            </div>
          </div>

          {/* Right column: text + CTA */}
          <div className="lp-hero-text">
            <span className="lp-hero-badge">AI-Powered Recruitment</span>
            <h1>Jesse, de Digitale Recruiter</h1>
            <p className="lp-hero-sub">
              De beste recruiter van Nederland — nu als AI. Bouw pakkende vacatureteksten
              in een fractie van de tijd, met de tone-of-voice die kandidaten in beweging krijgt.
            </p>
            <div className="lp-hero-actions">
              {!isAuthenticated ? (
                <>
                  <button
                    className="btn-cta btn-cta--hero"
                    onClick={() => loginWithRedirect({ appState: { returnTo: '/app' } })}
                  >
                    <span className="material-icons">chat</span>
                    Direct beginnen
                  </button>
                  <button
                    className="btn-hero-secondary"
                    onClick={() => document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Probeer gratis
                  </button>
                </>
              ) : (
                <Link className="btn-cta btn-cta--hero" to="/app">
                  <span className="material-icons">chat</span>
                  Open de chat
                </Link>
              )}
            </div>
          </div>
        </motion.div>
      </header>

      {/* ===== STATS STRIP ===== */}
      <motion.section
        className="lp-stats"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="stat-item">
          <span className="material-icons stat-icon">description</span>
          <span className="stat-number">500+</span>
          <span className="stat-label">Vacatures geschreven</span>
        </div>
        <div className="stat-item">
          <span className="material-icons stat-icon">schedule</span>
          <span className="stat-number">10 min</span>
          <span className="stat-label">Per vacaturetekst</span>
        </div>
        <div className="stat-item">
          <span className="material-icons stat-icon">verified</span>
          <span className="stat-number">24/7</span>
          <span className="stat-label">Altijd beschikbaar</span>
        </div>
      </motion.section>

      {/* ===== FEATURE SHOWCASE (alternating rows with images) ===== */}
      <section id="features" className="lp-section lp-showcase" ref={featRef}>
        <motion.h2
          className="section-title"
          initial="hidden"
          animate={featInView ? 'show' : 'hidden'}
          variants={revealItem}
        >
          Wat doet Jesse?
        </motion.h2>

        {/* Row 1: screenshot left, text right */}
        <motion.div className="showcase-row" initial="hidden" whileInView="show" viewport={{ once: true, margin: '-10%' }} variants={revealParent}>
          <motion.div className="showcase-media" variants={revealItem}>
            <img src={screen1} alt="Jesse schrijft een vacaturetekst op basis van je input" />
          </motion.div>
          <motion.div className="showcase-text" variants={revealItem}>
            <span className="showcase-tag showcase-tag--blue">AI-Schrijfstijl</span>
            <h3>Vacatures in de stijl van Jesse</h3>
            <p>De tone-of-voice van de beste recruiter van Nederland: helder, menselijk en to-the-point. Jesse schrijft alsof hij er zelf bij zit.</p>
            <ul className="showcase-bullets">
              <li><span className="material-icons">check_circle</span>Directe, menselijke schrijfstijl</li>
              <li><span className="material-icons">check_circle</span>Vakjargon? Jesse begrijpt het</li>
              <li><span className="material-icons">check_circle</span>Van technisch tot creatief, elke branche</li>
            </ul>
          </motion.div>
        </motion.div>

        {/* Row 2: text left, screenshot right (reversed) */}
        <motion.div className="showcase-row showcase-row--reverse" initial="hidden" whileInView="show" viewport={{ once: true, margin: '-10%' }} variants={revealParent}>
          <motion.div className="showcase-media" variants={revealItem}>
            <img src={screen2} alt="Van een kort gesprek naar een volledige vacaturetekst" />
          </motion.div>
          <motion.div className="showcase-text" variants={revealItem}>
            <span className="showcase-tag showcase-tag--green">Spraak naar tekst</span>
            <h3>Van belletje naar tekst in 10 minuten</h3>
            <p>Geen uren schrijven. Start een gesprek met Jesse, vertel over de functie, en je volledige vacaturetekst staat er binnen een paar minuten.</p>
            <ul className="showcase-bullets">
              <li><span className="material-icons">check_circle</span>Praat gewoon, Jesse luistert en schrijft</li>
              <li><span className="material-icons">check_circle</span>Interactief: Jesse stelt slimme vervolgvragen</li>
              <li><span className="material-icons">check_circle</span>Resultaat direct kopieerbaar en deelbaar</li>
            </ul>
          </motion.div>
        </motion.div>

        {/* Row 3: decorative visual left, text right */}
        <motion.div className="showcase-row" initial="hidden" whileInView="show" viewport={{ once: true, margin: '-10%' }} variants={revealParent}>
          <motion.div className="showcase-media showcase-media--decorative" variants={revealItem}>
            <div className="decorative-card">
              <div className="decorative-step">
                <span className="material-icons">upload_file</span>
                <span>Upload</span>
              </div>
              <div className="decorative-arrow">
                <span className="material-icons">trending_flat</span>
              </div>
              <div className="decorative-step">
                <span className="material-icons">auto_fix_high</span>
                <span>Verbeter</span>
              </div>
              <div className="decorative-arrow">
                <span className="material-icons">trending_flat</span>
              </div>
              <div className="decorative-step decorative-step--done">
                <span className="material-icons">check_circle</span>
                <span>Klaar!</span>
              </div>
            </div>
          </motion.div>
          <motion.div className="showcase-text" variants={revealItem}>
            <span className="showcase-tag showcase-tag--purple">Tekst verbeteren</span>
            <h3>Verbeter je bestaande teksten</h3>
            <p>Heb je al een vacaturetekst? Upload hem en Jesse optimaliseert de structuur, toon en aantrekkingskracht. Daarna kun je in de chat verder verfijnen.</p>
            <ul className="showcase-bullets">
              <li><span className="material-icons">check_circle</span>Upload PDF, Word of plak je tekst</li>
              <li><span className="material-icons">check_circle</span>Concrete verbetervoorstellen, niet vaag</li>
              <li><span className="material-icons">check_circle</span>Verfijn verder via de chatfunctie</li>
            </ul>
          </motion.div>
        </motion.div>
      </section>

      {/* ===== ORIGIN (immersive section with photo collage) ===== */}
      <section id="origin" className="lp-section lp-origin-section">
        <motion.div className="origin-content" initial="hidden" whileInView="show" viewport={{ once: true, margin: '-5%' }} variants={revealParent}>

          {/* Left: text story */}
          <motion.div className="origin-story" variants={revealItem}>
            <span className="showcase-tag showcase-tag--blue">Het verhaal</span>
            <h2>Wie is Jesse Geul?</h2>
            <p className="origin-lead">
              De beste recruiter van Nederland kan niet overal tegelijk zijn. Daarom bouwden we zijn digitale tweeling.
            </p>
            <p>
              Jesse Geul is auteur van het boek <em>"Waarom niemand bij je wil werken"</em>, spreker op recruitment-congressen
              door heel Nederland, en bekend als dé recruiter die vacatures schrijft waar kandidaten wél op reageren.
            </p>
            <p>
              Zijn geheim? Geen saaie opsommingen, maar eerlijke teksten in gewone mensentaal. Deze digitale Jesse kent de ins
              & outs van uiteenlopende vakgebieden en vertaalt vakjargon moeiteloos naar pakkende teksten.
            </p>
            <div className="origin-meta">
              <img src={humanaizeLogo} alt="HumanAIze" className="origin-logo" />
              <span>Een product van HumanAIze</span>
            </div>
          </motion.div>

          {/* Right: photo collage */}
          <motion.div className="origin-photos" variants={revealItem}>
            <div className="origin-collage">
              <div className="collage-main">
                <img src={jessePortrait} alt="Jesse Geul — portretfoto" />
              </div>
              <div className="collage-secondary">
                <div className="collage-item collage-item--congres">
                  <img src={jesseCongres} alt="Jesse op het podium bij een recruitment-congres" />
                </div>
                <div className="collage-item collage-item--casual">
                  <img src={jesseSmile} alt="Jesse Geul" />
                </div>
              </div>
            </div>
            <p className="collage-caption">Jesse Geul — auteur, spreker & recruiter</p>
          </motion.div>
        </motion.div>
      </section>

      {/* ===== SCREENSHOT MARQUEE (bridge: dark → light) ===== */}
      <ScreenshotMarquee />

      {/* ===== PLANS ===== */}
      <PlanCards
        selected={plan}
        onSelect={setPlan}
        onContinue={() => document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' })}
      />


      {/* ===== CTA / FORM ===== */}
      <section id="signup" className="lp-section lp-cta">
        <motion.div className="cta-surface" initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <div className="cta-head">
            <h2>Start je gratis proefperiode</h2>
            <p>Probeer Jesse 7 dagen gratis. Voor het Personal plan betaal je daarna €49/maand. Je kunt elk moment opzeggen.</p>
          </div>

          <form
            className="lp-form"
            onSubmit={(e) => {
              e.preventDefault();

              // Enterprise plan: mailto (maatwerk offerte)
              if (plan === 'enterprise') {
                window.location.href = mailtoHref;
                return;
              }

              // Personal plan: direct naar Stripe checkout
              handleAnonymousCheckout(e);
            }}
          >
            <div className="grid">
              <label className="field">
                <span>Naam</span>
                <input
                  type="text"
                  placeholder="Voor- en achternaam"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </label>

              <label className="field">
                <span>E-mail</span>
                <input
                  type="email"
                  placeholder="jij@bedrijf.nl"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </label>

              <label className="field">
                <span>Telefoon (optioneel)</span>
                <input
                  type="tel"
                  placeholder="06-…"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </label>
            </div>

            {/* Abonnement (mooie segmented buttons) */}
            <fieldset className="field plan-field">
              <span>Abonnement</span>
              <div className="segmented" role="tablist" aria-label="Abonnement">
                <button
                  type="button"
                  role="tab"
                  aria-selected={plan === 'personal'}
                  className={`seg-item ${plan === 'personal' ? 'is-active' : ''}`}
                  onClick={() => setPlan('personal')}
                >
                  <span className="seg-title">Personal</span>
                  <span className="seg-sub">€49/mnd</span>
                </button>

                <button
                  type="button"
                  role="tab"
                  aria-selected={plan === 'enterprise'}
                  className={`seg-item ${plan === 'enterprise' ? 'is-active' : ''}`}
                  onClick={() => setPlan('enterprise')}
                >
                  <span className="seg-title">Enterprise</span>
                  <span className="seg-sub">maatwerk</span>
                </button>
              </div>

              {/* Houd voor formulier/analytics eventueel nog een value aan */}
              <input type="hidden" name="plan" value={plan} />
            </fieldset>


            <div className="actions is-centered">
              <MagneticButton type="submit" disabled={checkoutLoading}>
                {checkoutLoading ? 'Laden...' : (plan === 'enterprise' ? 'Offerte Aanvragen' : 'Start 7 Dagen Gratis →')}
              </MagneticButton>
            </div>

            {checkoutError && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: '8px',
                color: '#dc2626',
                fontSize: '14px',
                textAlign: 'center'
              }}>
                {checkoutError}
              </div>
            )}

            <small className="privacy-note">
              {plan === 'enterprise'
                ? 'We nemen contact met je op voor een persoonlijk gesprek over de Enterprise mogelijkheden.'
                : 'Start je gratis proefperiode van 7 dagen. Geen creditcard vereist vooraf.'}
            </small>
          </form>
        </motion.div>
      </section>

      {/* ===== FAQ (cards i.p.v. dropdowns) ===== */}
      <section className="lp-section">
        <motion.h2 className="section-title" initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          Veelgestelde vragen
        </motion.h2>
        <div className="faq-grid">
          <FAQCard q="Is er een gratis proef?" a="Ja, je kunt een paar dagen testen met een proefaccount." />
          <FAQCard q="Welke data gebruikt Jesse?" a="Jesse gebruikt de beste AI modellen, op een veilige manier en verwijdert alle data weer na je gesprekken." />
          <FAQCard q="Werkt het op mobiel?" a="Ja, er is een fijne mobiele interface die snel en simpel werkt." />
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="lp-footer">
        <nav className="footer-links">
          <button
            onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }}
            className="text-link"
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            Privacy
          </button>
          <span className="dot">•</span>
          <button
            onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
            className="text-link"
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            Voorwaarden
          </button>
        </nav>
        <div className="footer-meta">
          <span>© {new Date().getFullYear()} Jesse Geul & HumanAIze</span>
          <span className="dot">•</span>
          <a className="text-link" href="https://www.jessegeul.nl/" target="_blank" rel="noreferrer">jessegeul.nl</a>
          <span className="dot">•</span>
          <a className="text-link" href="https://humanaize.nl/" target="_blank" rel="noreferrer">humanaize.nl</a>
        </div>
      </footer>

      {/* ===== MODALS ===== */}
      <PrivacyModal isVisible={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />
      <TermsModal isVisible={showTermsModal} onClose={() => setShowTermsModal(false)} />
    </main>
  );
}
